/**
 * Calculates how many time elements can fit in the container width
 * without overflowing, accounting for the truncated items indicator.
 *
 * @param containerWidth - Available width in pixels
 * @param times - Array of time strings (e.g., ["14:30", "18:45"])
 * @param getIndicatorText - Function to get translated indicator text for a given count
 * @returns Number of visible items that fit in the container
 */
export function calculateVisibleTimeItems(
  containerWidth: number,
  times: string[],
  getIndicatorText: (count: number) => string,
): number {
  if (times.length === 0 || containerWidth <= 0) {
    return times.length;
  }

  const ITEM_GAP = 8; // gap-2 = 8px

  // Create a temporary element to measure text width
  const tempElement = createTempButtonElement();
  document.body.appendChild(tempElement);

  // Measure indicator width once using the maximum possible count
  // This gives us the longest possible indicator width
  const maxIndicatorWidth = measureIndicatorWidth(
    times.length,
    getIndicatorText,
  );

  const visibleCount = calculateVisibleCount(
    times,
    containerWidth,
    tempElement,
    maxIndicatorWidth,
    ITEM_GAP,
  );

  document.body.removeChild(tempElement);
  return visibleCount;
}

/**
 * Calculates how many time items can fit in the container width
 */
function calculateVisibleCount(
  times: string[],
  containerWidth: number,
  tempElement: HTMLButtonElement,
  maxIndicatorWidth: number,
  itemGap: number,
): number {
  let totalWidth = 0;
  let visibleCount = 0;

  // Try to fit as many items as possible
  for (let i = 0; i < times.length; i++) {
    // Measure the full width of this time string
    tempElement.textContent = times[i];
    const timeWidth = tempElement.offsetWidth;

    // Calculate width needed for this item including gap
    const itemWidthWithGap = totalWidth + timeWidth + (i > 0 ? itemGap : 0);

    // Check if this is the last item (no indicator needed)
    const isLastItem = i === times.length - 1;

    if (isLastItem) {
      // For the last item, check if it fits without indicator
      if (itemWidthWithGap <= containerWidth) {
        visibleCount++;
      }
      break;
    } else {
      // For non-last items, check if item + indicator fits
      const requiredWidth = itemWidthWithGap + itemGap + maxIndicatorWidth;

      if (requiredWidth <= containerWidth) {
        totalWidth = itemWidthWithGap;
        visibleCount++;
      } else {
        // Item doesn't fit with indicator, stop here
        break;
      }
    }
  }

  return visibleCount;
}

/**
 * Creates a temporary button element for measuring text width
 */
function createTempButtonElement(): HTMLButtonElement {
  const tempElement = document.createElement('button');
  tempElement.className = 'text-text-neutral-primary whitespace-nowrap text-sm';
  tempElement.style.visibility = 'hidden';
  tempElement.style.position = 'absolute';
  tempElement.style.pointerEvents = 'none';
  return tempElement;
}

/**
 * Measures the width of the truncated items indicator element
 */
function measureIndicatorWidth(
  count: number,
  getIndicatorText: (count: number) => string,
): number {
  const indicatorElement = document.createElement('span');
  indicatorElement.className =
    'text-text-neutral-tertiary bg-situational-light whitespace-nowrap rounded-2xl px-2 py-1 text-xs font-medium';
  indicatorElement.style.visibility = 'hidden';
  indicatorElement.style.position = 'absolute';
  indicatorElement.style.pointerEvents = 'none';
  indicatorElement.textContent = getIndicatorText(count);
  document.body.appendChild(indicatorElement);
  const width = indicatorElement.offsetWidth;
  document.body.removeChild(indicatorElement);
  return width;
}
